'use client';
import { useState, useRef, useEffect, useCallback } from 'react';

declare global {
  interface Window {
    google: any;
    _expertsMapsCallbacks: (() => void)[];
    _expertsMapReady: boolean;
  }
}

const GOOGLE_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY!;

const CATEGORIES = [
  'חשמלאי','אינסטלטור','עורך דין','רואה חשבון','מתקין דלתות',
  'נגר','צבעי','טכנאי מזגנים','שרברב','גנן','בוני בריכות','אחר'
];

// Shared Maps loader — handles multiple components
function loadGoogleMaps(callback: () => void) {
  if (typeof window === 'undefined') return;
  if (window._expertsMapReady && window.google?.maps?.places) {
    callback();
    return;
  }
  if (!window._expertsMapsCallbacks) window._expertsMapsCallbacks = [];
  window._expertsMapsCallbacks.push(callback);

  if (!document.querySelector('script[data-experts-maps]')) {
    (window as any).__expertsMapsLoaded = () => {
      window._expertsMapReady = true;
      (window._expertsMapsCallbacks || []).forEach(fn => { try { fn(); } catch {} });
      window._expertsMapsCallbacks = [];
    };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&language=he&callback=__expertsMapsLoaded&loading=async`;
    s.async = true;
    s.defer = true;
    s.dataset.expertsMaps = '1';
    document.head.appendChild(s);
  }
}

export default function JoinForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ businessName: '', category: '', googleUrl: '', phone: '', city: '', lat: '', lng: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [citySelected, setCitySelected] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const cityRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  // Initialize Places autocomplete (can run on any step, attaches when cityRef available)
  const initAutocomplete = useCallback(() => {
    if (!cityRef.current || !window.google?.maps?.places || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(cityRef.current, {
      types: ['(cities)'],
      componentRestrictions: { country: 'il' },
      fields: ['name', 'geometry'],
      language: 'he',
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const name = place.name || '';
      const lat = place.geometry?.location?.lat() || '';
      const lng = place.geometry?.location?.lng() || '';
      setForm(f => ({ ...f, city: name, lat: String(lat), lng: String(lng) }));
      setCitySelected(true);
      // Update map
      if (lat && lng && mapInstance.current) {
        const pos = { lat: Number(lat), lng: Number(lng) };
        mapInstance.current.setCenter(pos);
        mapInstance.current.setZoom(14);
        if (markerRef.current) markerRef.current.setPosition(pos);
        else {
          markerRef.current = new window.google.maps.Marker({
            position: pos,
            map: mapInstance.current,
            title: name,
          });
        }
      }
    });
    autocompleteRef.current = ac;
  }, []);

  // Initialize map (only when div is visible)
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || mapInstance.current) return;
    setMapLoaded(true);
    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 31.7683, lng: 35.2137 },
      zoom: 7,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ],
    });
  }, []);

  // Load Maps on mount
  useEffect(() => {
    loadGoogleMaps(() => {
      initAutocomplete();
    });
  }, [initAutocomplete]);

  // When step changes to 2, init autocomplete + map
  useEffect(() => {
    if (step !== 2) return;
    const timer = setTimeout(() => {
      if (window.google?.maps?.places) {
        initAutocomplete();
        initMap();
      } else {
        loadGoogleMaps(() => {
          initAutocomplete();
          initMap();
        });
      }
    }, 150); // small delay to ensure DOM is ready
    return () => clearTimeout(timer);
  }, [step, initAutocomplete, initMap]);

  const submit = async () => {
    if (!form.businessName || !form.category || !form.phone) { setError('יש למלא שם עסק, תחום וטלפון'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch('/api/join', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) setDone(true);
      else setError('שגיאה בשליחה, נסה שוב');
    } catch { setError('שגיאת רשת'); }
    setSubmitting(false);
  };

  if (done) return (
    <div className="card p-12 text-center" style={{ maxWidth: 460, margin: '0 auto' }}>
      <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl mx-auto mb-5"
        style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', boxShadow: '0 8px 24px rgba(34,197,94,.3)' }}>
        ✓
      </div>
      <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--color-text-main)' }}>הבקשה התקבלה!</h2>
      <p className="text-base mb-6" style={{ color: 'var(--color-text-muted)' }}>
        ניצור איתך קשר ב-24 שעות הקרובות ונשאב את המלצות הגוגל שלך.
      </p>
      <p className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>📲 {form.phone}</p>
    </div>
  );

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Progress bar */}
      <div className="flex items-center mb-8 gap-0">
        {['פרטי העסק','מיקום','גוגל ביזנס'].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label} className="flex items-center flex-1">
              <button
                onClick={() => n < step && setStep(n)}
                className="flex items-center gap-2 group"
                style={{ cursor: n < step ? 'pointer' : 'default' }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 transition-all"
                  style={{
                    background: done ? '#22c55e' : active ? 'var(--color-primary)' : 'var(--color-surface-2)',
                    color: (done || active) ? '#fff' : 'var(--color-text-muted)',
                    border: `2px solid ${done ? '#22c55e' : active ? 'var(--color-primary)' : 'var(--color-card-border)'}`,
                    boxShadow: active ? '0 0 0 4px rgba(13,71,161,.15)' : 'none',
                  }}
                >
                  {done ? '✓' : n}
                </div>
                <span className="hidden sm:block text-xs font-semibold" style={{ color: active ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                  {label}
                </span>
              </button>
              {i < 2 && (
                <div className="flex-1 h-0.5 mx-2" style={{ background: step > n ? '#22c55e' : 'var(--color-card-border)' }} />
              )}
            </div>
          );
        })}
      </div>

      <div className="card p-6 sm:p-8">
        {/* STEP 1 — Business details */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-black mb-0.5" style={{ color: 'var(--color-text-main)' }}>פרטי העסק</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>כמה פרטים בסיסיים — 2 דקות</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-text-main)' }}>שם העסק *</label>
                <input className="input-field" placeholder='כהן חשמל בע"מ' value={form.businessName}
                  onChange={e => set('businessName', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-text-main)' }}>תחום *</label>
                <select className="input-field" value={form.category} onChange={e => set('category', e.target.value)}>
                  <option value="">בחר תחום...</option>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-text-main)' }}>טלפון ליצירת קשר *</label>
                <input className="input-field" type="tel" placeholder="05X-XXXXXXX" dir="ltr"
                  value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                onClick={() => {
                  if (form.businessName && form.category && form.phone) { setError(''); setStep(2); }
                  else setError('יש למלא את כל השדות המסומנים *');
                }}
                className="btn-primary w-full justify-center py-3.5"
              >
                המשך ← מיקום
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — Location + Map */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-black mb-0.5" style={{ color: 'var(--color-text-main)' }}>מיקום העסק</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>הקלד עיר — תופיע רשימת הצעות אוטומטית</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-text-main)' }}>
                  🏙️ עיר (עם השלמה אוטומטית מגוגל)
                </label>
                <div className="relative">
                  <input
                    ref={cityRef}
                    className="input-field pr-10"
                    placeholder="הקלד שם עיר..."
                    defaultValue={form.city}
                    autoComplete="off"
                    onFocus={() => {
                      // Re-init autocomplete if not yet done
                      if (!autocompleteRef.current && window.google?.maps?.places) initAutocomplete();
                    }}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg">📍</span>
                </div>
                {citySelected && (
                  <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: '#22c55e' }}>
                    <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">✓</span>
                    <strong>{form.city}</strong> נבחרה
                    {form.lat && <span style={{ color: 'var(--color-text-muted)' }}>
                      &nbsp;· {Number(form.lat).toFixed(4)}, {Number(form.lng).toFixed(4)}
                    </span>}
                  </p>
                )}
                {!citySelected && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    🔍 התחל להקליד — יופיעו הצעות עירייה
                  </p>
                )}
              </div>

              {/* Google Map */}
              <div
                ref={mapRef}
                className="w-full rounded-2xl overflow-hidden relative"
                style={{
                  height: 220,
                  border: '2px solid var(--color-card-border)',
                  background: 'var(--color-surface-2)',
                }}
              >
                {!mapLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ color: 'var(--color-text-muted)' }}>
                    <div className="text-center">
                      <div className="text-3xl mb-2">🗺️</div>
                      <p className="text-sm">טוען מפה...</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setError(''); setStep(1); }} className="btn-ghost flex-1 justify-center py-3">← חזור</button>
                <button onClick={() => { setError(''); setStep(3); }} className="btn-primary flex-1 justify-center py-3">המשך → גוגל</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Google URL + Summary */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-black mb-0.5" style={{ color: 'var(--color-text-main)' }}>קישור גוגל ביזנס</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>כדי שנוכל לשאוב את המלצות הגוגל שלך (לא חובה)</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-text-main)' }}>🗺️ קישור גוגל מפות</label>
                <input
                  className="input-field text-sm"
                  type="url"
                  placeholder="https://www.google.com/maps/place/..."
                  dir="ltr"
                  value={form.googleUrl}
                  onChange={e => set('googleUrl', e.target.value)}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  Google Maps → שתף → העתק קישור
                </p>
              </div>

              {/* Summary */}
              <div className="p-4 rounded-xl space-y-2" style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-card-border)' }}>
                <p className="text-xs font-bold mb-1" style={{ color: 'var(--color-text-muted)' }}>סיכום הבקשה:</p>
                {[
                  ['🏢', 'עסק', form.businessName],
                  ['🔧', 'תחום', form.category],
                  ['📞', 'טלפון', form.phone],
                  ['📍', 'עיר', form.city || '—'],
                ].map(([icon, label, val]) => (
                  <div key={label} className="flex items-center gap-2 text-sm">
                    <span>{icon}</span>
                    <span style={{ color: 'var(--color-text-muted)' }}>{label}:</span>
                    <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{val}</span>
                  </div>
                ))}
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => { setError(''); setStep(2); }} className="btn-ghost flex-1 justify-center py-3">← חזור</button>
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="btn-primary flex-1 justify-center py-3"
                  style={{ opacity: submitting ? .7 : 1 }}
                >
                  {submitting ? '⏳ שולח...' : '🚀 שלח בקשה'}
                </button>
              </div>
              <p className="text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
                🔒 גנת פרטיות מלאה · שירות חינמי לתמיד
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
